import {useCallback, useEffect, useRef} from 'react'

/**
 * Hook that synchronizes programmatic .value changes
 * (e.g. from dictation extensions) with React's onChange.
 *
 * Usage:
 *   const handleFocus = useDictationSync();
 *   <input onFocus={handleFocus} />
 */
export function useDictationPatch() {
  const lastValueRef = useRef<string>(null);

  const onFocus = useCallback((event: FocusEvent) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;
    lastValueRef.current = input.value;
  }, []);

  const onBlur = useCallback((event: FocusEvent) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;
    const prev = lastValueRef.current;
    const curr = input.value;
    if (curr !== prev) {
      // Dispatch a real input event so React's onChange fires
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }
    lastValueRef.current = null;
  }, []);

  // Optional cleanup (not strictly necessary here)
  useEffect(() => () => {
    lastValueRef.current = null;
  }, []);

  return { onFocus, onBlur };
}